import React from "react";
import { NoteQuiz, Btn } from "@/components/games/NoteQuiz";
import { StaffViewer } from "@/components/StaffViewer";
import { TabView, Fretboard, POSITIONS, STRINGS, noteAt, playAt, ChordBox, strum } from "@/components/games/GuitarParts";
import { playFreq, audioClock } from "@/lib/audio";
import { pitchToFreq } from "@/lib/music";
import { openMic, detectPitch, freqToMidi, midiName } from "@/lib/mic";

const LETTERS = ["A", "B", "C", "D", "E", "F", "G"];
const TREBLE = ["C4", "D4", "E4", "F4", "G4", "A4", "B4", "C5", "D5", "E5", "F5", "G5"];
const rand = (a) => a[Math.floor(Math.random() * a.length)];
const fresh = (a, prev, key = (x) => x) => { let x; do { x = rand(a); } while (prev && key(x) === prev.key && a.length > 1); return x; };
const tone = (p, d = 0.9) => playFreq(pitchToFreq(p), d);

const Staff = ({ clef = "treble", note }) => (
  <div className="w-44"><StaffViewer clef={clef} notes={[note]} width={170} playOnClick={false} testId="ng-staff" /></div>
);

const staveGame = (id, label, clef = "treble", range = TREBLE) => ({
  id, label, tip: `A note appears on the ${clef} stave. Tap its letter name.`,
  make: (prev) => {
    const p = fresh(range, prev);
    return { key: p, prompt: <Staff clef={clef} note={{ pitch: p, duration: "whole" }} />, choices: LETTERS, check: (c) => c === p[0], reveal: [p[0]], play: () => tone(p) };
  },
});
const BASS = ["C2", "D2", "E2", "F2", "G2", "A2", "B2", "C3", "D3", "E3", "F3", "G3", "A3", "B3", "C4", "D4", "E4"];

const HL = ["C4", "D4", "E4", "F4", "G4", "A4", "B4", "C5"];
const higherLower = {
  id: "higher-lower", label: "Higher or lower", tip: "Listen to two notes. Is the second note higher or lower than the first?",
  make: () => {
    const a = rand(HL); let b; do { b = rand(HL); } while (b === a);
    const ans = HL.indexOf(b) > HL.indexOf(a) ? "Higher" : "Lower";
    const play = () => { tone(a, 0.7); setTimeout(() => tone(b, 0.7), 750); };
    return { key: a + b, prompt: <p className="text-sm text-muted-foreground">Was the second note higher or lower?</p>, choices: ["Higher", "Lower"], check: (c) => c === ans, reveal: [ans], play, auto: true };
  },
};

const NATURAL = POSITIONS.filter(({ s, f }) => !noteAt(s, f).includes("#"));
const tabGame = {
  id: "tab", label: "Notes on TAB", tip: "TAB shows a string and a fret number (0 means open string). Tap the letter name of that note.",
  make: (prev) => {
    const pos = fresh(NATURAL, prev, (x) => x.id);
    const l = noteAt(pos.s, pos.f);
    return { key: pos.id, prompt: <TabView s={pos.s} f={pos.f} />, choices: LETTERS, check: (c) => c === l, reveal: [l], play: () => playAt(pos.s, pos.f) };
  },
};
const fretGame = {
  id: "fretboard", label: "Find it on the fretboard", tip: "Tap any place on the neck (open strings up to fret 4) that plays this note.",
  make: (prev) => {
    const l = fresh(LETTERS, prev);
    const FB = (props) => <Fretboard {...props} />;
    return { key: l, prompt: <p className="text-sm text-muted-foreground">Find: <span className="ml-1 font-serif text-4xl text-primary" data-testid="ng-target">{l}</span></p>,
      Input: FB, check: (id) => { const [s, f] = id.split("-").map(Number); return noteAt(s, f) === l; },
      reveal: NATURAL.filter(({ s, f }) => noteAt(s, f) === l).map((x) => x.id) };
  },
};

const writtenMidi = (p) => Math.round(69 + 12 * Math.log2(pitchToFreq(p) / 440));
const cellTone = (id, mark) => (mark.reveal?.includes(id) ? "#22C55E" : mark.wrong === id ? "#FCA5A5" : mark.hit === id ? "#86EFAC" : null);
// Tap-to-write TAB: string lines (thinnest on top) with fret numbers 0-4 to choose from.
const TabPicker = ({ onPick, mark, strs = STRINGS, sound = playAt }) => (
  <svg viewBox={`0 0 330 ${strs.length * 22 + 18}`} className="mt-4 w-full max-w-md select-none" data-testid="ng-tab-picker" role="group" aria-label="TAB: tap a fret number on a string line">
    {[...strs].reverse().map((st, r) => <g key={st.n}><text x="6" y={20 + r * 22} fontSize="11" fill="#A8A29E" fontFamily="monospace">{st.n}</text><line x1="22" y1={16 + r * 22} x2="324" y2={16 + r * 22} stroke="var(--staff-line, #78716C)" /></g>)}
    {strs.flatMap((_, s) => [0, 1, 2, 3, 4].map((f) => ({ s, f, id: `${s}-${f}` }))).map(({ s, f, id }) => {
      const x = 60 + f * 60, y = 16 + (strs.length - 1 - s) * 22, c = cellTone(id, mark);
      const tap = () => { sound(s, f); onPick(id); };
      return (
        <g key={id} onClick={tap} style={{ cursor: "pointer" }} role="button" tabIndex={0} aria-label={`String ${6 - s}, fret ${f}`} data-testid={`tab-cell-${id}`}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); tap(); } }}>
          <rect x={x - 11} y={y - 9} width="22" height="18" rx="4" fill={c || "hsl(var(--card))"} stroke={c ? "#14532D" : "#57534E"} />
          <text x={x} y={y + 4} textAnchor="middle" fontSize="12" fontWeight="700" fill={c ? "#0B0D12" : "#F59E0B"} fontFamily="monospace">{f}</text>
        </g>
      );
    })}
  </svg>
);
const WRITE_RANGE = ["C4", "D4", "E4", "F4", "G4", "A4", "B4", "C5", "D5", "E5", "F5", "G5"];
const writeTabGame = {
  id: "write-tab", label: "Write it on the TAB", tip: "A note appears on the stave. Tap the fret number on the right TAB line (string) to play it. Some notes can go in more than one place.",
  make: (prev) => {
    const p = fresh(WRITE_RANGE, prev), m = writtenMidi(p);
    const ok = POSITIONS.filter(({ s, f }) => STRINGS[s].midi + f + 12 === m).map((x) => x.id);
    return { key: p, prompt: <Staff note={{ pitch: p, duration: "whole" }} />, Input: TabPicker, check: (id) => ok.includes(id), reveal: ok, play: () => tone(p) };
  },
};

const BASS_STR = [{ n: "E", midi: 28 }, { n: "A", midi: 33 }, { n: "D", midi: 38 }, { n: "G", midi: 43 }];
const BASS_RANGE = ["G2", "A2", "B2", "C3", "D3", "E3", "F3", "G3", "A3", "B3", "C4"];
const BassPicker = (props) => <TabPicker {...props} strs={BASS_STR} sound={(s, f) => playFreq(440 * 2 ** ((BASS_STR[s].midi + 12 + f - 69) / 12), 0.9)} />;
const bassTabGame = {
  id: "bass-tab", label: "Bass line: write it on bass TAB", tip: "Bassists: a note appears on the bass clef. Tap its fret on the 4-string bass TAB (top line = G string).",
  make: (prev) => {
    const p = fresh(BASS_RANGE, prev), m = writtenMidi(p);
    const ok = BASS_STR.flatMap((st, s) => [0, 1, 2, 3, 4].filter((f) => st.midi + f + 12 === m).map((f) => `${s}-${f}`));
    return { key: p, prompt: <Staff clef="bass" note={{ pitch: p, duration: "whole" }} />, Input: BassPicker, check: (id) => ok.includes(id), reveal: ok, play: () => tone(p) };
  },
};

// Tap a line or space on the stave. slots: pitches offered; step 0 = bottom line (E4).
const stepOf = (p) => (Number(p.slice(-1)) - 4) * 7 + "CDEFGAB".indexOf(p[0]) - 2;
const makeStavePicker = (slots, clef = "treble") => function StavePicker({ onPick, mark }) {
  return (
    <svg viewBox={`0 0 ${70 + slots.length * 26} 120`} className="mt-4 w-full max-w-md select-none" data-testid="ng-stave-picker" role="group" aria-label="Stave: tap a line or space">
      {[0, 1, 2, 3, 4].map((k) => <line key={k} x1="10" y1={30 + k * 12} x2={60 + slots.length * 26} y2={30 + k * 12} stroke="var(--staff-line, #78716C)" />)}
      <text x="14" y={clef === "percussion" ? 70 : 78} fontSize={clef === "percussion" ? 13 : 48} fill="#A8A29E" fontFamily={clef === "percussion" ? "monospace" : "'Noto Music', serif"}>{clef === "percussion" ? "||" : "𝄞"}</text>
      {slots.map((p, k) => {
        const y = 78 - stepOf(p) * 6, x = 50 + k * 26, c = cellTone(p, mark);
        const ledger = y >= 90 || y <= 18;
        return (
          <g key={p} onClick={() => onPick(p)} style={{ cursor: "pointer" }} role="button" tabIndex={0} aria-label={`Slot ${k + 1}`} data-testid={`stave-slot-${p}`}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPick(p); } }}>
            <rect x={x - 12} y={y - 6} width="24" height="12" fill="transparent" />
            {ledger && <line x1={x - 11} y1={y} x2={x + 11} y2={y} stroke="var(--staff-line, #78716C)" />}
            <ellipse cx={x} cy={y} rx="7" ry="5" fill={c || "transparent"} stroke={c ? "#14532D" : "#57534E"} strokeDasharray={c ? "" : "2 2"} />
          </g>
        );
      })}
    </svg>
  );
};
const WriteTreble = makeStavePicker(UP_TO_A_SLOTS());
function UP_TO_A_SLOTS() { return ["C4", "D4", "E4", "F4", "G4", "A4", "B4", "C5", "D5", "E5", "F5", "G5", "A5"]; }
const writeStaveGame = {
  id: "write-stave", label: "Write it on the stave", tip: "Tap a line or space on the treble stave that has this letter name. Some letters appear twice — either is right.",
  make: (prev) => {
    const l = fresh(LETTERS, prev), ok = UP_TO_A_SLOTS().filter((p) => p[0] === l);
    return { key: l, prompt: <p className="text-sm text-muted-foreground">Write: <span className="ml-1 font-serif text-4xl text-primary" data-testid="ng-target">{l}</span></p>, Input: WriteTreble, check: (p) => ok.includes(p), reveal: ok, play: () => tone(ok[0]) };
  },
};

const SingInput = ({ target, onPick }) => {
  const [state, setState] = React.useState("idle");
  const [heard, setHeard] = React.useState("");
  const live = React.useRef(null);
  React.useEffect(() => () => live.current?.(), []);
  const go = async () => {
    let m;
    try { m = await openMic(); } catch { setState("blocked"); return; }
    setState("listening");
    let raf, run = 0, last = "";
    const stop = () => { cancelAnimationFrame(raf); m.stop(); live.current = null; };
    live.current = stop;
    const until = performance.now() + 6000;
    const loop = () => {
      const f = detectPitch(m.read(), m.ctx.sampleRate);
      if (f && f > 60 && f < 1300) { const nm = midiName(freqToMidi(f)).replace(/\d/g, ""); setHeard(nm); run = nm === last ? run + 1 : 1; last = nm; if (nm === target && run >= 8) { stop(); onPick("ok"); return; } }
      if (performance.now() > until) { stop(); onPick(last || "none"); return; }
      raf = requestAnimationFrame(loop);
    };
    loop();
  };
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="ng-sing">
      <Btn primary onClick={go} testId="ng-sing-start">{state === "listening" ? "Listening… hold the note" : "Sing it (microphone)"}</Btn>
      {heard && <span className="text-sm text-muted-foreground" data-testid="ng-sing-heard">I hear: <b className="text-foreground">{heard}</b></span>}
      {state === "blocked" && <span className="text-sm text-amber-400">Allow the microphone in your browser and try again.</span>}
    </div>
  );
};
const singGame = {
  id: "sing-note", label: "Sing the note", tip: "Press Hear it if you need help, then press Sing it and hold the note on 'ah'. Any octave is fine — it's the letter name that counts.",
  make: (prev) => {
    const p = fresh(["C4", "D4", "E4", "F4", "G4", "A4", "B4"], prev);
    const In = ({ onPick }) => <SingInput target={p[0]} onPick={onPick} />;
    return { key: p, prompt: <Staff note={{ pitch: p, duration: "whole" }} />, Input: In, check: (id) => id === "ok", reveal: [], play: () => tone(p) };
  },
};

const KIT = "/media/samples/kit";
const DRUMS = {
  "Hi-hat": { file: "hhc_vl3_rr1.mp3", note: { pitch: "G5", head: "x", duration: "quarter" } },
  Snare: { file: "snare_vl4_rr1.mp3", note: { pitch: "C5", duration: "quarter" } },
  Kick: { file: "kick_vl3_rr1.mp3", note: { pitch: "F4", duration: "quarter" } },
  Crash: { file: "crash_vl2_rr1.mp3" },
  Tom: { file: "tomhi_vl3_rr1.mp3" },
};
const bufs = {};
const hit = async (name) => {
  const ac = audioClock();
  const u = `${KIT}/${DRUMS[name].file}`;
  bufs[u] = bufs[u] || await ac.decodeAudioData(await (await fetch(u)).arrayBuffer());
  const src = ac.createBufferSource(); src.buffer = bufs[u]; src.connect(ac.destination); src.start();
};
const SCORE_DRUMS = ["Hi-hat", "Snare", "Kick"];
const DRUM_SLOTS = { "Bass drum": "F4", "Floor tom": "A4", Snare: "C5", "Mid tom": "D5", "High tom": "E5", "Hi-hat": "G5", Crash: "A5" };
const WriteDrum = makeStavePicker(Object.values(DRUM_SLOTS), "percussion");
const writeDrumGame = {
  id: "write-drum", label: "Write it on the drum score", tip: "Tap the line or space on the drum score where this drum or cymbal is written.",
  make: (prev) => {
    const d = fresh(Object.keys(DRUM_SLOTS), prev);
    return { key: d, prompt: <p className="text-sm text-muted-foreground">Write: <span className="ml-1 font-serif text-3xl text-primary" data-testid="ng-target">{d}</span></p>, Input: WriteDrum, check: (p) => p === DRUM_SLOTS[d], reveal: [DRUM_SLOTS[d]] };
  },
};
const GROOVES = {
  "Rock beat": [["Hi-hat", "Kick"], ["Hi-hat"], ["Hi-hat", "Snare"], ["Hi-hat"], ["Hi-hat", "Kick"], ["Hi-hat"], ["Hi-hat", "Snare"], ["Hi-hat"]],
  "Four on the floor": [["Hi-hat", "Kick"], ["Hi-hat"], ["Hi-hat", "Snare", "Kick"], ["Hi-hat"], ["Hi-hat", "Kick"], ["Hi-hat"], ["Hi-hat", "Snare", "Kick"], ["Hi-hat"]],
  "Half-time": [["Hi-hat", "Kick"], ["Hi-hat"], ["Hi-hat"], ["Hi-hat"], ["Hi-hat", "Snare"], ["Hi-hat"], ["Hi-hat"], ["Hi-hat"]],
};
const COUNTS = ["1", "&", "2", "&", "3", "&", "4", "&"];
const grooveScore = (g) => <div className="w-64"><StaffViewer clef="percussion" width={260} playOnClick={false} testId="ng-groove-score"
  notes={GROOVES[g].map((hits, k) => ({ pitches: hits.map((h) => DRUMS[h].note.pitch), label: COUNTS[k], duration: "eighth", stemDir: "up" }))} /></div>;
const grooveGame = {
  id: "match-groove", label: "Match the groove", tip: "Listen to the beat (press Hear it to play it again), then tap the drum score that matches.",
  make: (prev) => {
    const g = fresh(Object.keys(GROOVES), prev), ids = [];
    const stop = () => { ids.forEach(clearTimeout); ids.length = 0; };
    const play = () => { stop(); [0, 1].forEach((bar) => GROOVES[g].forEach((hits, k) => hits.forEach((h) => ids.push(setTimeout(() => hit(h).catch(() => {}), (bar * 8 + k) * 260))))); };
    return { key: g, prompt: <p className="text-sm text-muted-foreground">Which drum score did you hear?</p>, choices: Object.keys(GROOVES), render: grooveScore, check: (c) => c === g, reveal: [g], play, stop, auto: true };
  },
};
const drumScoreGame = {
  id: "drum-score", label: "Name the drum on the score", tip: "A hit appears on the drum score. Is it the hi-hat, snare or kick?",
  make: (prev) => {
    const d = fresh(SCORE_DRUMS, prev);
    return { key: d, prompt: <Staff clef="percussion" note={DRUMS[d].note} />, choices: SCORE_DRUMS, check: (c) => c === d, reveal: [d] };
  },
};
const drumSoundGame = {
  id: "drum-sound", label: "Which drum is it?", tip: "Listen to the sound. Which drum or cymbal was it?",
  make: (prev) => {
    const d = fresh(Object.keys(DRUMS), prev);
    return { key: d, prompt: <p className="text-sm text-muted-foreground">Which drum did you hear?</p>, choices: Object.keys(DRUMS), check: (c) => c === d, reveal: [d], play: () => hit(d).catch(() => {}), auto: true };
  },
};

const QUALITIES = [["", "major"], ["m", "minor"], ["dim", "diminished"], ["aug", "augmented"], ["7", "dominant 7th"], ["maj7", "major 7th"], ["m7", "minor 7th"], ["sus2", "suspended 2nd"], ["sus4", "suspended 4th"], ["5", "power chord"]];
const ROOTS = ["C", "D", "E", "F", "G", "A", "B"];
const pickChord = () => { const r = rand(ROOTS), q = rand(QUALITIES); return { sym: r + q[0], name: `${r} ${q[1]}`, r }; };
const optionsFor = (ans, make) => { const set = new Set([ans]); while (set.size < 4) set.add(make()); return [...set].sort(() => Math.random() - 0.5); };
const qPick = () => rand(QUALITIES);
const symbolGame = {
  id: "chord-symbol", label: "Symbol to name", tip: "Read the chord symbol. Tap its full name.",
  make: (prev) => {
    const c = fresh([0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(pickChord), prev, (x) => x.sym);
    return { key: c.sym, prompt: <p className="font-serif text-4xl text-primary" data-testid="ng-target">{c.sym}</p>, choices: optionsFor(c.name, () => `${c.r} ${qPick()[1]}`), check: (x) => x === c.name, reveal: [c.name] };
  },
};
const nameGame = {
  id: "chord-name", label: "Name to symbol", tip: "Read the chord name. Tap the chord symbol that matches it.",
  make: (prev) => {
    const c = fresh([0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(pickChord), prev, (x) => x.sym);
    return { key: c.sym, prompt: <p className="font-serif text-3xl text-primary" data-testid="ng-target">{c.name}</p>, choices: optionsFor(c.sym, () => c.r + qPick()[0]), check: (x) => x === c.sym, reveal: [c.sym] };
  },
};
const SHAPES = { C: ["x", 3, 2, 0, 1, 0], G: [3, 2, 0, 0, 0, 3], D: ["x", "x", 0, 2, 3, 2], A: ["x", 0, 2, 2, 2, 0], E: [0, 2, 2, 1, 0, 0], Am: ["x", 0, 2, 2, 1, 0], Em: [0, 2, 2, 0, 0, 0], Dm: ["x", "x", 0, 2, 3, 1] };
const shapeGame = {
  id: "chord-shape", label: "Name the chord shape", tip: "Look at the chord box (low E string on the left). Tap its chord symbol. Press Hear it to strum it.",
  make: (prev) => {
    const name = fresh(Object.keys(SHAPES), prev);
    return { key: name, prompt: <ChordBox name="?" frets={SHAPES[name]} testId="ng-chordbox" />, choices: optionsFor(name, () => rand(Object.keys(SHAPES))), check: (x) => x === name, reveal: [name], play: () => strum(SHAPES[name]) };
  },
};

const SETS = {
  singers: [staveGame("stave", "Name the note on the stave"), higherLower, writeStaveGame, singGame],
  guitarists: [staveGame("stave", "Name the note on the stave"), fretGame, tabGame, writeTabGame],
  drummers: [drumScoreGame, drumSoundGame, writeDrumGame, grooveGame],
  "singers-bass": [staveGame("bass-stave", "Name the note: bass clef", "bass", BASS)],
  "guitarists-bass": [staveGame("bass-stave", "Name the note: bass clef", "bass", BASS), bassTabGame],
  "guitarists-chords": [symbolGame, nameGame, shapeGame],
  pianists: [staveGame("stave", "Name the note on the stave"), staveGame("bass-stave", "Name the note: bass clef", "bass", BASS), higherLower, writeStaveGame],
};

// Lesson 1 warm-up games for singers, guitarists and drummers.
const WHERE = { singers: "Singers Lesson 1", guitarists: "Guitar Lesson 1", drummers: "Drums Lesson 1", "singers-bass": "Singers Lesson 2", "guitarists-bass": "Guitar Lesson 2", "guitarists-chords": "Guitar Lesson 10", pianists: "Gospel Piano" };
const CHORDS = { "guitarists-chords": true };
export const NoteGames = ({ instrument, where }) => <NoteQuiz title={CHORDS[instrument] ? "Chord games" : "Note games"} unit={CHORDS[instrument] ? "chord" : "note"} games={SETS[instrument]} testId={`note-games-${instrument}`} where={where || WHERE[instrument]} scoreKey={where ? `note-games-${instrument}-${where.toLowerCase().replace(/\W+/g, "-")}` : undefined} />;
